import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@features/auth/store/authStore';
import appInsightsService from '../../services/appInsights/appInsights.service';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

export const apiClient = axios.create({
  baseURL,
  // Must be >= backend's ALUDRA_API_TIMEOUT (60s) so the frontend never
  // gives up before the backend does. Previously 30s — caused false
  // "timeout" errors when Aludra was slow, making users retry and
  // eventually hit the rate limiter.
  timeout: 65000, // 65 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Para cookies de refresh token
});

// Función para limpiar autenticación y redirigir al login
const handleAuthFailure = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('sessionId');
  localStorage.removeItem('selectedCompanyId');
  localStorage.removeItem('authUser');
  localStorage.removeItem('auth-storage');
  localStorage.removeItem('invoice-filter-storage');

  // Redirigir al login con indicador de sesión expirada
  if (window.location.pathname !== '/login') {
    window.location.href = '/login?expired=1';
  }
};

function shouldKeepSessionOnRefreshFailure(error: unknown): boolean {
  const axiosError = error as AxiosError | undefined;
  const status = axiosError?.response?.status;

  return status === 503 || status === 440 || !axiosError?.response;
}

// Single shared refresh promise — prevents race conditions
let refreshPromise: Promise<string> | null = null;

// Request interceptor - Agregar token y sessionId a las peticiones
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Prefer this tab's own in-memory session (Zustand) over the shared
    // localStorage value. localStorage is shared across ALL tabs, so if the
    // same user has a duplicate session open in another tab (e.g. logs in
    // again there), localStorage gets overwritten with that OTHER session's
    // token/sessionId — sending it here would silently swap this tab onto
    // the wrong session. Falls back to localStorage only when this tab's
    // own state hasn't been hydrated yet (e.g. very first request after a
    // hard refresh, before ProtectedRoute finishes rehydrating).
    const accessToken = useAuthStore.getState().accessToken || localStorage.getItem('accessToken');
    const sessionId = useAuthStore.getState().sessionId || localStorage.getItem('sessionId');

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Send sessionId in header for multi-session support
    if (sessionId && config.headers) {
      config.headers['x-session-id'] = sessionId;
    }

    // Correlation ID para trazabilidad end-to-end: el mismo ID aparece en
    // App Insights del frontend (trackException) y en cada trace del backend.
    const correlationId = crypto.randomUUID();
    config.headers['X-Correlation-ID'] = correlationId;
    (config as any)._correlationId = correlationId;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Manejar refresh token automáticamente
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // 503 = Service unavailable (Redis down, etc.) — do NOT logout, just propagate the error
    // The user's session is still valid, the server just can't reach Redis momentarily
    if (error.response?.status === 503) {
      // Se sale antes del trackException de más abajo, así que sin esto el
      // error queda invisible tanto en consola como en App Insights (el
      // console.warn se reenvía como Trace vía setupConsoleInterceptor).
      console.warn('[apiClient] 503 Service Unavailable', {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        correlationId: (error.config as any)?._correlationId || 'none',
        responseData: error.response?.data,
      });
      return Promise.reject(error);
    }

    // 440 = external service session expired/unavailable.
    // Keep the portal session alive so the UI can degrade gracefully.
    if (error.response?.status === 440) {
      console.warn('[apiClient] 440 External service session expired', {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        correlationId: (error.config as any)?._correlationId || 'none',
      });
      return Promise.reject(error);
    }

    // 401 = Our JWT expired — try refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;

      // If a refresh is already in progress, ALL requests wait on the same promise
      // No race condition: refreshPromise is set synchronously before any await
      if (!refreshPromise) {
        refreshPromise = (async () => {
          try {
            const { data } = await apiClient.post('/auth/refresh');
            const { accessToken } = data;

            try { localStorage.setItem('accessToken', accessToken); } catch { /* storage full — Zustand state is enough */ }
            useAuthStore.setState({ accessToken });

            return accessToken;
          } catch (refreshError) {
            if (!shouldKeepSessionOnRefreshFailure(refreshError)) {
              handleAuthFailure();
            }
            throw refreshError;
          } finally {
            refreshPromise = null;
          }
        })();
      }

      try {
        const token = await refreshPromise;
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return apiClient(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // 401 on /auth/refresh → session is truly dead, clean up and redirect
    // 401 on /auth/login → just wrong credentials, do NOT clear storage/redirect
    //   (the login form handles this error and shows the message to the user)
    if (error.response?.status === 401 && originalRequest.url?.includes('/auth/refresh')) {
      handleAuthFailure();
      return Promise.reject(error);
    }

    // Peticion cancelada a proposito (el usuario disparo una busqueda nueva
    // y React Query aborto la anterior via AbortSignal, ver useInvoices.ts) —
    // no es un error real, es la cancelacion funcionando como se espera. Sin
    // este corte, cada busqueda reemplazada se reportaba como excepcion en
    // App Insights, ensuciando las alertas con "errores" que nunca lo fueron.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Track failed API calls in Application Insights
    try {
      appInsightsService.trackException(error, {
        url: error.config?.url || 'unknown',
        method: error.config?.method?.toUpperCase() || 'unknown',
        status: String(error.response?.status || 0),
        correlationId: (error.config as any)?._correlationId || 'none',
        requestId: (error.response?.headers?.['x-request-id'] as string) || 'none',
      });
    } catch { /* telemetry must never break the app */ }

    return Promise.reject(error);
  }
);
